import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import translations from "@/i18n/translations";

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    // English, because index.html is English: the title, description and
    // <html lang> a crawler or a first-time visitor meets all say so. Defaulting
    // to Ukrainian meant Google rendered Ukrainian copy under English metadata,
    // and anyone arriving from an English search result landed on a page they
    // could not read. Nobody who has already chosen is affected - their choice
    // is in localStorage and wins here.
    return localStorage.getItem("mealpill_lang") || "en";
  });
  const langChangeListeners = useRef(new Set());

  const t = useCallback(
    (key) => {
      return translations[lang]?.[key] || translations["en"]?.[key] || key;
    },
    [lang]
  );

  const onLangChange = useCallback((callback) => {
    langChangeListeners.current.add(callback);
    return () => langChangeListeners.current.delete(callback);
  }, []);

  const switchLang = useCallback(async (newLang) => {
    setLang(newLang);
    localStorage.setItem("mealpill_lang", newLang);

    // Persist to backend so all AI endpoints use correct language
    const token = localStorage.getItem("mealpill_token");
    if (token) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/language`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ language: newLang }),
        });
      } catch (e) {
        console.warn("Failed to persist language:", e);
      }
    }

    langChangeListeners.current.forEach((cb) => cb(newLang));
}, []);

  return (
    <LanguageContext.Provider value={{ lang, t, switchLang, onLangChange }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
